import { deepCleanOptional } from '@luna/utils';

import type { QueryParameters } from './types/NavigateOptions';
import type { NavigateArgs } from './types/NavigateArgsType';
import { splitPathFromQueryParams } from './query/QueryParamsService';
import { generatePath } from './generatePath';
import { createQueryString } from './query/createQueryString';
import { getCurrentRouterStrategy } from './routerTypeStrategy';

export type BaseCreateLinkOptions = {
    /**
     * Query parameters to append to the path.
     */
    queryParameters?: QueryParameters;
};

export type CreateLinkOptions<Path extends string> =
    NavigateArgs<Path> extends never
        ? [] | [BaseCreateLinkOptions]
        : [NavigateArgs<Path> & BaseCreateLinkOptions];

/**
 * Creates a fully qualified link path.
 *
 * @template Path - The type of the path string
 * @param {Path} path - The base path template (e.g., "/user/:id")
 * @param {CreateLinkOptions<Path>} [options] - Options including path parameters and query parameters
 * @returns {string} The fully qualified link path
 *
 * @example
 * ```typescript
 * // Basic usage
 * const link = createUnsafeLinkPath('/user/:id', { id: '123' });
 * // Output: "/user/123"
 *
 * // With query parameters
 * const linkWithQuery = createUnsafeLinkPath('/search', {
 *   queryParameters: { q: 'example', page: '1' }
 * });
 * // Output: "/search?q=example&page=1"
 *
 * // Combining path params and query params
 * const complexLink = createUnsafeLinkPath('/user/:id/posts', {
 *   id: '123',
 *   queryParameters: { sort: 'date' }
 * });
 * // Output: "/user/123/posts?sort=date"
 * ```
 */
export const createUnsafeLinkPath = <Path extends string>(
    path: Path,
    ...[options]: CreateLinkOptions<Path>
) => {
    const { queryParameters = {}, ...pathParams } = options ?? {};

    const [basePath, baseQueryParams] = splitPathFromQueryParams(path);

    const targetHref = generatePath(basePath as string, pathParams);

    const queryString = createQueryString(
        deepCleanOptional({
            ...baseQueryParams,
            ...queryParameters,
        }),
    );

    return getCurrentRouterStrategy().ensureAbsolutePath(targetHref) + queryString;
};

/**
 * Creates a link path with route type-safety.
 *
 * @param {string} path - The path template
 * @param {CreateLinkOptions<'/>'>} [options] - Options including query parameters
 * @returns {string} The generated link path
 *
 */
export function createLinkPath(path: '/', ...[options]: CreateLinkOptions<'/'>): string {
    // @ts-expect-error invalid type
    return createUnsafeLinkPath(path, options);
}
